import React from 'react';
import { VellumIcon } from './VellumIcon';
import { VellumWordmark } from './VellumWordmark';

type LogoVariant = 'horizontal' | 'stacked';

interface VellumLogoProps {
  /**
   * - `horizontal` (default): icon to the left of the wordmark, side by side.
   * - `stacked`: icon centred above the wordmark.
   */
  variant?: LogoVariant;
  /**
   * Controls the overall size. The icon and wordmark scale together.
   * This sets the icon height; the wordmark height is derived proportionally.
   */
  iconHeight?: number;
  /** Override the dark-element fill color for both icon and wordmark. */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Composite Vellum logo that pairs the feather-V icon with the wordmark.
 * Supports horizontal (default) and stacked layout variants.
 * Both sub-components automatically adapt to light / dark colour mode.
 */
export function VellumLogo({
  variant = 'horizontal',
  iconHeight = 32,
  color,
  className,
  style,
}: VellumLogoProps) {
  // Wordmark height is tuned to optically match the icon cap-height.
  // The wordmark SVG is 907×250 — we want it to look proportionate next to the
  // icon which is ~312×336. A wordmark height of ~55% of icon height works well.
  const wordmarkHeight = Math.round(iconHeight * 0.65);

  if (variant === 'stacked') {
    return (
      <div
        className={className}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: Math.round(iconHeight * 0.2),
          ...style,
        }}
      >
        <VellumIcon height={iconHeight} color={color} />
        <VellumWordmark height={wordmarkHeight} color={color} />
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: Math.round(iconHeight * 0.3333),
        ...style,
      }}
    >
      <VellumIcon height={iconHeight} color={color} />
      <VellumWordmark height={wordmarkHeight} color={color} style={{ marginTop: '10px' }} />
    </div>
  );
}
